import React, { useState } from "react";

import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  CheckForm,
  Grid,
  Group,
  InlineAlert,
  Input,
  Link,
  PositiveNumberInput,
  Section,
  Select,
  TemperatureSelect,
  Tooltip,
} from "@samskip/frontend-components";
import { Control, UseFormRegister, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import {
  ShippingTypeBookingTemplates,
  CommonConstants,
  BookingTemplateConstants,
} from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import { useAppSelector } from "@assets/hooks";
import {
  CommonTypes,
  CargoPackageParentType,
  Commodity,
  SelectOption,
} from "@assets/types";
import { ShipmentUtilities } from "@assets/utilities";
import {
  commodityLabelMapper,
  commodityValueMapper,
  packageTypeLabelMapper,
  packageTypeValueMapper,
} from "@assets/utilities/labelMapperUtils";
import { isPackageRefrigerated } from "@assets/utilities/packageUtilities";
import { WithFormController } from "@components";
import { commoditiesState } from "@state/slices/commoditySlice";

type CargoDetailsSectionProps = {
  control: Control<BookingTemplateFormTypes.CargoForm>;
  index: number;
  isRemovable: boolean;
  onCommodityChange: CommonTypes.OneParamCallbackFunction<Commodity | null>;
  onPackageTypeChange: CommonTypes.OneParamCallbackFunction<CargoPackageParentType | null>;
  onRemove: CommonTypes.CallbackFunction;
  onTemperatureChange: CommonTypes.OneParamCallbackFunction<SelectOption<number> | null>;
  onTemperatureControlChange: React.ChangeEventHandler<HTMLInputElement>;
  onTemperatureOutOfBounds: CommonTypes.CallbackFunction;
  register: UseFormRegister<BookingTemplateFormTypes.CargoForm>;
  shipmentPackageTypes: CargoPackageParentType[];
  shippingType?: ShippingTypeBookingTemplates;
};

const CargoDetailsSection = ({
  control,
  index,
  isRemovable,
  onCommodityChange,
  onPackageTypeChange,
  onRemove,
  onTemperatureChange,
  onTemperatureControlChange,
  onTemperatureOutOfBounds,
  register,
  shipmentPackageTypes,
  shippingType,
}: CargoDetailsSectionProps) => {
  const { t } = useTranslation();

  const commodities = useAppSelector(commoditiesState);

  const shipmentPackagingValue = useWatch({
    control,
    name: `Details.${index}.ShipmentPackaging` as const,
  });

  const commodityValue = useWatch({
    control,
    name: `Details.${index}.Commodity` as const,
  });

  const isOverSizedValue = useWatch({
    control,
    name: `Details.${index}.OversizeFlag` as const,
  });

  const isTemperatureControlledValue = useWatch({
    control,
    name: `Details.${index}.Refrigerated` as const,
  });

  const temperatureOption = useWatch({
    control,
    name: `Details.${index}.Temperature` as const,
  });

  const [temperatureOptions, setTemperatureOptions] = useState<
    SelectOption<number>[]
  >(CommonConstants.defaultCargoTemperatureOptions);

  const handleCommodityChange = (commodity: Commodity | null) => {
    let newShipmentTemperatures =
      CommonConstants.defaultCargoTemperatureOptions;

    if (commodity) {
      newShipmentTemperatures =
        ShipmentUtilities.getShipmentTemperatures(commodity);
    }
    setTemperatureOptions(newShipmentTemperatures);

    const found = newShipmentTemperatures.some(
      (option) => option.value === temperatureOption?.value
    );
    if (!found) {
      onTemperatureOutOfBounds();
    }

    onCommodityChange(commodity);
  };

  const isOversizedEnabled =
    (!isTemperatureControlledValue && !shipmentPackagingValue?.PackageCode) ||
    shipmentPackagingValue?.IsOversized;

  const isTemperatureEnabled =
    (!isOverSizedValue && !shipmentPackagingValue?.PackageCode) ||
    shippingType === ShippingTypeBookingTemplates.LL;

  const getShipmentPackagingOptions = (): CargoPackageParentType[] => {
    if (isOverSizedValue) {
      return shipmentPackageTypes.filter((cType) => cType.IsOversized);
    }
    if (isTemperatureControlledValue) {
      return shipmentPackageTypes.filter((cType) =>
        isPackageRefrigerated(cType)
      );
    }
    return shipmentPackageTypes;
  };

  return (
    <Section>
      <Grid mdCol={2}>
        <WithFormController
          control={control}
          name={`Details.${index}.Commodity` as const}
        >
          <Select
            fullWidth
            getOptionLabel={commodityLabelMapper}
            getOptionValue={commodityValueMapper}
            id={`detail-${index}-commodity`}
            isClearable
            label={t("LABEL_COMMODITY")}
            onChange={handleCommodityChange}
            options={commodities}
            placeholder={t("PLACEHOLDER_SELECT_COMMODITY")}
            portaling
          />
        </WithFormController>
        <div className="flex flex-col gap-8 items-end md:flex-row">
          <Input
            disabled={!commodityValue}
            disabledTooltipContent={t("ERROR_SELECT_COMMODITY_FIRST")}
            fullWidth
            id={`cargo-description-${index}`}
            label={t("LABEL_CARGO_DESCRIPTION")}
            maxLength={BookingTemplateConstants.MAX_INPUT_LENGTH_CARGO_DESC}
            placeholder={t("PLACEHOLDER_CARGO_DESCRIPTION_OPTIONAL")}
            {...register(`Details.${index}.CargoDescription` as const)}
          />
          {isRemovable && (
            <Link
              className="pb-[0.625rem]"
              icon={faTrash}
              noUnderline
              onClick={onRemove}
            >
              {t("LABEL_DELETE")}
            </Link>
          )}
        </div>
      </Grid>
      <Group title={t("LABEL_SPECIAL_HANDLING")}>
        <CheckForm
          id={`hazardous-${index}`}
          label={t("LABEL_HAZARDOUS_MATERIALS")}
          {...register(`Details.${index}.Hazardous` as const)}
        />
        {shippingType !== ShippingTypeBookingTemplates.LL && (
          <Tooltip
            color="neutral-900"
            content={t("INFO_OVERSIZED_DISABLED")}
            hidden={isOversizedEnabled}
          >
            <CheckForm
              id={`oversized-${index}`}
              label={t("LABEL_OVERSIZED")}
              {...register(`Details.${index}.OversizeFlag` as const)}
              disabled={!isOversizedEnabled}
            />
          </Tooltip>
        )}
        <Tooltip
          color="neutral-900"
          content={t("INFO_TEMPERATURE_AUTOMATICALLY_SELECTED")}
          direction="bottom"
          hidden={isTemperatureEnabled}
        >
          <CheckForm
            disabled={!isTemperatureEnabled}
            id={`temperatureControlled-${index}`}
            label={t("LABEL_TEMPERATURE_CONTROLLED")}
            {...register(`Details.${index}.Refrigerated`, {
              onChange: onTemperatureControlChange,
            })}
          />
        </Tooltip>
      </Group>
      {isTemperatureControlledValue && (
        <>
          <Grid mdCol={4}>
            <WithFormController
              control={control}
              name={`Details.${index}.Temperature` as const}
            >
              <TemperatureSelect
                fullWidth
                getOptionLabel={(temp: SelectOption<number>) => temp.label}
                getOptionValue={(temp: SelectOption<number>) =>
                  temp.value.toString() ?? ""
                }
                id={`detail-${index}-temperature`}
                isClearable
                label={t("LABEL_TEMPERATURE")}
                onChange={onTemperatureChange}
                options={temperatureOptions}
                placeholder={t("PLACEHOLDER_SELECT_TEMPERATURE")}
              />
            </WithFormController>
            {temperatureOption && temperatureOption.value > 0 && (
              <PositiveNumberInput
                id={`ventilation_volume-${index}`}
                label={t("LABEL_VENTILATION_VOLUME")}
                placeholder={t("PLACEHOLDER_VENTILATION")}
                {...register(`Details.${index}.VentilationVolume` as const)}
              />
            )}
          </Grid>
          {temperatureOption && (
            <Grid mdCol={4}>
              <InlineAlert>
                {temperatureOption.value > 0 ? (
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="INFO_POSITIVE_TEMPERATURE"
                  />
                ) : temperatureOption.value === 0 ? (
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="INFO_ZERO_TEMPERATURE"
                  />
                ) : (
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="INFO_NEGATIVE_TEMPERATURE"
                  />
                )}
              </InlineAlert>
            </Grid>
          )}
        </>
      )}
      <Grid mdCol={2}>
        <WithFormController
          control={control}
          name={`Details.${index}.ShipmentPackaging` as const}
        >
          <Select
            fullWidth
            getOptionLabel={packageTypeLabelMapper}
            getOptionValue={packageTypeValueMapper}
            id={`detail-${index}-packaging`}
            isClearable
            label={
              shippingType === ShippingTypeBookingTemplates.LL
                ? t("LABEL_PACKAGE_TYPE")
                : t("LABEL_CONTAINER_TYPE")
            }
            onChange={onPackageTypeChange}
            options={getShipmentPackagingOptions()}
            placeholder={
              shippingType === ShippingTypeBookingTemplates.LL
                ? t("PLACEHOLDER_SELECT_PACKAGE_TYPE")
                : t("PLACEHOLDER_SELECT_CONTAINER_TYPE")
            }
            portaling
          />
        </WithFormController>
      </Grid>
    </Section>
  );
};

export default CargoDetailsSection;
